<template>
  <!-- NOSONAR -->
  <div
    class="border-solid border-lightSlate border-[1px] md:p-[20px] p-[13px] md:w-[452px] w-[257px] rounded-[20px] bg-white"
    :class="[
      percentage ? 'md:!w-[400px] !w-[300px]' : '',
      { 'opacity-60': isReadonly },
    ]"
    data-test-id="card"
    tabindex="0"
  >
    <div
      class="flex justify-between md:gap-[16px] gap-[10px] items-start md:flex-row flex-col"
    >
      <div class="flex flex-col md:gap-[12px] gap-[8px] md:w-full w-full">
        <div class="relative w-[57px] h-[57px]">
          <div
            class="w-full h-full rounded-full border border-blueNeutral flex items-center justify-center overflow-hidden"
          >
            <img
              :src="imageUrl"
              :alt="`Img-${name}`"
              class="md:w-full md:h-full w-[40px] h-[40px] object-cover rounded-full"
            />
          </div>
          <div
            class="absolute left-[48px] top-[15px] flex items-center gap-[4px]"
          >
            <img
              v-if="icon"
              :src="icon"
              :alt="`Icon-${name}`"
              class="md:h-[30px] md:w-[30px] h-[25px] w-[25px]"
            />

            <p
              class="md:text-[14px] text-[10px] text-greyLight font-just tracking-[-0.7%] whitespace-nowrap"
            >
              {{ tag }}
            </p>
          </div>
        </div>
        <div class="flex justify-between w-full items-center">
          <p
            class="font-just md:text-[19.12px] text-[10.5px] font-[600] text-darkBlue capitalize"
          >
            {{ name }}
          </p>
        </div>
      </div>
      <div class="flex flex-col md:items-end items-center gap-[12px] md:w-full">
        <div class="flex gap-[8px] items-center">
          <BaseInput
            :label="resolvedLabel"
            v-model="form.cash"
            class="md:w-[140px] !h-[48px] !w-full"
            :class="{ 'pointer-events-none opacity-50': isReadonly }"
            :allowDecimal="!percentage"
            :decimalPlaces="2"
            :maxLength="maxLength"
            :readonly="isReadonly"
            :max="percentage ? maxAllowedPercentage : undefined"
            @input="handleInput"
            :icon="percentage ? '%' : '$'"
            :iconPosition="!percentage ? 'left' : 'right'"
            :data-test-id="`percentage-input`"
            type="number"
            :digitOnly="percentage"
            :ariaLabel="percentage ? $t('remainderDistributionLabels.percentage') : $t('wishesLabel.amount')"
          />
          <BaseDelete
            v-if="form.cash && !isReadonly"
            class="inline-flex"
            :header="percentage ? $t('wishesLabel.removeBeneficiary') : $t('wishesLabel.clearAmount')"
            :items="[
              {
                label: percentage ? $t('commonLabels.remove') : $t('commonLabels.clear'),
                danger: true,
                onClick: (closePopover) => handleClear(closePopover),
              },
            ]"
            :disabled="!form.cash || isReadonly"
          >
            <template #trigger>
              <div
                v-html="deleteIcon"
                ref="trigger"
                class="transition-colors duration-200 cursor-pointer"
                :class="{
                  'opacity-50 cursor-not-allowed': !form.cash || isReadonly,
                }"
              ></div>
              <span class="sr-only">{{ $t('commonLabels.remove') }} {{ percentage ? $t('wishesLabel.beneficiary') : $t('wishesLabel.clearAmount') }}</span>
            </template>
            <template #item-icon>
              <div v-html="deleteIcon" class="w-5 h-5 mr-2"></div>
            </template>
          </BaseDelete>
          <div
            v-else
            v-html="deleteIcon"
            class="transition-colors duration-200 cursor-not-allowed"
          ></div>
        </div>
      </div>
    </div>
    <div
      class="md:mt-[7.65px] mt-[5px] border-[1.2px] md:mb-[8px] mb-[6px] border-solid border-whiteBlue md:block hidden w-full"
    ></div>

    <div class="flex gap-[8px] items-center md:mt-[12px] mt-[8px]">
      <div
        v-if="currentAmountSet"
        v-html="checkedIcon"
        class="flex items-center"
      ></div>
      <p
        class="md:text-[14.34px] text-[10px] font-just font-500 tracking-[-0.7%]"
        :class="currentAmountSet ? 'text-deepSky' : 'text-progress'"
        v-if="!percentage"
      >
        {{ currentAmountSet ? $t('wishesLabel.amountSet') : $t('wishesLabel.noValueAssign') }}
      </p>
      <p
        class="md:text-[14.34px] text-[10px] font-just font-500 tracking-[-0.7%]"
        :class="currentAmountSet ? 'text-deepSky' : 'text-progress'"
        v-if="percentage"
      >
        {{
          currentAmountSet
            ? $t('wishesLabel.remainderDistributionSet')
            : $t('wishesLabel.noPercentageAssigned')
        }}
      </p>
    </div>

    <div v-if="validationError" class="mt-2">
      <p class="text-red-500 text-sm font-just">{{ validationError }}</p>
    </div>
    <div
      class="md:pt-[16.47px] pt-[10px]"
      :class="percentage ? 'hidden' : 'block'"
    >
      <BaseButton
        class="w-full !bg-transparent !text-btnActive transition-colors duration-200"
        :label="$t('documentLabels.save')"
        :class="
          isSaveButtonEnabled
            ? '!border-black !border-[1px] !border-solid cursor-pointer'
            : '!border-whiteBlue !border-[1px] !border-solid !cursor-not-allowed pointer-events-none'
        "
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import BaseDelete from "@/components/Base/BaseDelete.vue";
import { deleteSvg, shareSvg, checkedRightCircleSvg } from "@/utils/commonSvg";
import { useWishesStore } from "@/stores/wishesStore";
import { BaseURL } from "@/api/apiService";

const { t } = useI18n();
const wishesStore = useWishesStore();
const validationError = ref("");
const isValidating = ref(false);
const imageUrl = ref("");
const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: String,
  tag: String,
  icon: String,
  label: {
    type: String,
    default: "wishesLabel.cashValue",
  },
  percentage: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: 12,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  maxAllowedPercentage: {
    type: Number,
    default: 100,
  },
  amountSet: {
    type: Boolean,
    default: false,
  },
  initialAmount: {
    type: Number,
    default: 0,
  },
});

// Translates the label prop — the default is an i18n key ("wishesLabel.cashValue"),
// but callers can also pass a plain string. t() returns the key itself as fallback
// if no translation is found, so plain strings pass through unchanged.
const resolvedLabel = computed(() => t(props.label, props.label));

const emit = defineEmits([
  "cash-change",
  "percentage-change",
  "share",
  "save",
  "validate-percentage",
  "delete-beneficiary",
  "input-change",
]);

const form = ref({ cash: "" });
const showValidationError = ref(false);
const amountSet = ref(false);

const initializeAmount = () => {
  if (props.initialAmount && props.initialAmount > 0) {
    form.value.cash = props.initialAmount.toString();
    amountSet.value = true;
  } else {
    form.value.cash = "";
    amountSet.value = false;
  }
};

watch(
  () => props.initialAmount,
  (newAmount) => {
    if (newAmount !== undefined) {
      form.value.cash = newAmount > 0 ? newAmount.toString() : "";
      amountSet.value = newAmount > 0;
    }
  },
  { immediate: true }
);

watch(
  () => props.amountSet,
  (newValue) => {
    amountSet.value = newValue;
  },
  { immediate: true }
);

watch(
  () => props.isReadonly,
  (newValue) => {
    if (newValue) {
      showValidationError.value = false;
    }
  }
);

const deleteIconColor = computed(() => {
  if (props.isReadonly) return "#C0C0C0";
  return form.value.cash ? "#863329" : "#8A9798";
});

const deleteIcon = computed(() =>
  deleteSvg({ width: "15", height: "17", color: deleteIconColor.value })
);

// Update the handleClear function in the CashBequestCardComponent
const handleClear = async (closePopover) => {
  if (props.isReadonly) return;

  try {
    // Call the delete API first
    if (props.percentage) {
      await wishesStore.deleteRemainder(props.id);
    } else {
      await wishesStore.deleteCashBequest(props.id);
    }

    // Clear local state immediately for visual feedback
    form.value.cash = "";
    amountSet.value = false;
    showValidationError.value = false;

    // After successful API call, emit delete event to parent to remove the card
    emit("delete-beneficiary", props.id);

    if (closePopover && typeof closePopover === "function") {
      closePopover();
    }
  } catch (error) {
    console.error("Error deleting value:", error);
    // Optionally show error message to user
    validationError.value = "Failed to delete. Please try again.";
  }
};

// Update the handleSubmit function in the CashBequestCardComponent (first component)
const handleSubmit = async () => {
  if (props.isReadonly || !form.value.cash) return;

  const numValue = parseFloat(form.value.cash) || 0;

  // Clear previous validation errors
  validationError.value = "";
  isValidating.value = true;

  // Validate zero assignment
  if (numValue <= 0) {
    validationError.value = "Please enter a value greater than 0";
    isValidating.value = false;
    return;
  }

  if (props.percentage) {
    // Get current allocations from parent component
    emit("validate-percentage", numValue, props.id, (error) => {
      if (error) {
        validationError.value = error;
        isValidating.value = false;
        return;
      }

      // If validation passes, proceed with save
      saveValue(numValue);
    });
  } else {
    saveValue(numValue);
  }
};

// Add this helper function
const saveValue = async (numValue) => {
  try {
    if (props.percentage) {
      const result = await wishesStore.updateRemainder(props.id, numValue);
      if (result.success) {
        emit("percentage-change", numValue);
        emit("update:initialAmount", numValue);
        emit("save", numValue);
        amountSet.value = true;
        validationError.value = "";
      }
    } else {
      emit("cash-change", numValue);
      amountSet.value = true;
      validationError.value = "";
    }
  } catch (error) {
    console.error("Error saving value:", error);
    validationError.value = "Failed to save. Please try again.";
    amountSet.value = false;
  } finally {
    isValidating.value = false;
  }
};

const handleInput = (event) => {
  if (props.isReadonly) return;

  let value = event?.target?.value ?? event ?? "";

  if (value === "") {
    form.value.cash = "";
    amountSet.value = false;
    emit("input-change", "");
    return;
  }

  value = value.toString();

  if (props.percentage) {
    // For percentage, allow any input but validate on save
    if (value.length > 3) {
      value = value.slice(0, 3);
    }
  } else if (value && !isNaN(parseFloat(value)) && parseFloat(value) < 0) {
    // For cash amounts, ensure non-negative values
    value = "0";
  }

  form.value.cash = value;
  emit("input-change", value);
};
const currentAmountSet = computed(() => {
  const numValue = parseFloat(form.value.cash) || 0;
  return numValue > 0 && numValue === props.initialAmount;
});

const isSaveButtonEnabled = computed(() => {
  if (!form.value.cash || props.isReadonly || showValidationError.value) {
    return false;
  }

  const numValue = parseFloat(form.value.cash) || 0;

  if (numValue <= 0) {
    return false;
  }

  return numValue !== props.initialAmount;
});
const updateInput = (value) => {
  form.value.cash = value.toString();
  amountSet.value = value > 0;
};

const clearInput = () => {
  if (props.isReadonly) return;
  form.value.cash = "";
  amountSet.value = false;
  showValidationError.value = false;
  if (props.percentage) {
    emit("percentage-change", 0);
  }
};

defineExpose({
  clearInput,
  updateInput,
});

onMounted(() => {
  initializeAmount();
});

const shareIcon = shareSvg({ width: "20", height: "20", color: "#8A9798" });
const checkedIcon = checkedRightCircleSvg({ width: "16", height: "16" });

watch(
  () => props.id,
  () => {
    imageUrl.value = `${BaseURL}/objects/${props.id}/thumbnail`;
  },
  { immediate: true }
);
</script>
